// askSmart-ui/src/pages/TOSP/components/AddBudgetModal.tsx
import React, { useEffect, useState } from "react";
import { nextBudgetName, validateBudgetName, validateYear } from "../validation";

interface AddBudgetModalProps {
  isOpen: boolean;
  /** Years already opened — only to pick a sensible default. */
  existingYears: number[];
  /**
   * What the other budgets are called, for the default name and the clash
   * check. When editing, the budget's own name must be left out of this so it
   * isn't reported as clashing with itself.
   */
  existingNames: string[];
  /** Set when editing rather than adding. */
  initial?: { year: number; name: string } | null;
  onClose: () => void;
  onConfirm: (year: number, name: string) => void;
}

// Adds or edits a budget. A year can be budgeted more than once — several
// drafts of the same year get worked up and the losers deleted — so the year is
// neither unique nor ordered. The NAME is what has to be unique: it's how the
// budgets are told apart.
export default function AddBudgetModal({
  isOpen,
  existingYears,
  existingNames,
  initial = null,
  onClose,
  onConfirm,
}: AddBudgetModalProps) {
  const [year, setYear] = useState("");
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<{ year?: string; name?: string }>({});

  const latestYear = existingYears.length ? Math.max(...existingYears) : null;
  // Compared by content, so re-rendering with a fresh array of the same names
  // can't re-run the effect and overwrite what's been typed.
  const takenKey = existingNames.join("|");

  const editingYear = initial?.year ?? null;
  const editingName = initial?.name ?? null;

  useEffect(() => {
    if (!isOpen) return;
    if (editingYear !== null && editingName !== null) {
      setYear(String(editingYear));
      setName(editingName);
    } else {
      // Another draft of the year already being worked on is the common case;
      // the first budget of all defaults to next year.
      setYear(String(latestYear ?? new Date().getFullYear() + 1));
      setName(nextBudgetName(existingNames));
    }
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, latestYear, takenKey, editingYear, editingName]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { year: parsed, error: yearError } = validateYear(year);
    const { name: parsedName, error: nameError } = validateBudgetName(name, existingNames);
    setErrors({ year: yearError, name: nameError });
    if (parsed === undefined || parsedName === undefined) return;
    onConfirm(parsed, parsedName);
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "400px" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>
            {initial ? "Edit Budget" : "Add Budget"}
          </h2>
        </div>
        <form className="smrtdocs-modal-body" onSubmit={handleSubmit} noValidate autoComplete="off">
          <div className="tosp-field" style={{ marginBottom: 16 }}>
            <label className="tosp-label" htmlFor="tosp-add-budget-name">
              Name <span className="tosp-required">*</span>
            </label>
            <input
              id="tosp-add-budget-name"
              className="manage-input"
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            {errors.name ? (
              <p className="tosp-field-error">{errors.name}</p>
            ) : (
              <p className="tosp-field-hint">
                How this budget is listed. Each budget needs its own name.
              </p>
            )}
          </div>

          <div className="tosp-field" style={{ marginBottom: 16 }}>
            <label className="tosp-label" htmlFor="tosp-add-budget-year">
              Budget Year <span className="tosp-required">*</span>
            </label>
            <input
              id="tosp-add-budget-year"
              className="manage-input"
              autoComplete="off"
              inputMode="numeric"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            />
            {errors.year && <p className="tosp-field-error">{errors.year}</p>}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save">
              {initial ? "Save" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
